import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { config } from '@/config';
import { fetchHealthReady, fetchHealthVersion } from '@/lib/health';
import { keys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/**
 * End-to-end proof that the dashboard, the proxy and the API are wired together:
 * it calls `GET /health/ready`, which performs real Postgres and Redis checks on
 * the API side (04 §4.11, 10 M0 exit criterion).
 *
 * Deliberately not behind auth — `/health/*` is public (07 §9.2), which is what
 * makes it usable before login exists.
 */
export function HealthBanner() {
  const { t } = useTranslation();

  const ready = useQuery({
    queryKey: keys.healthReady,
    queryFn: ({ signal }) => fetchHealthReady(signal),
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: 0,
  });

  const version = useQuery({
    queryKey: keys.healthVersion,
    queryFn: ({ signal }) => fetchHealthVersion(signal),
    staleTime: 5 * 60_000,
    retry: 0,
    // Only ask for the version once the API is known to answer at all.
    enabled: ready.data?.ok === true,
  });

  const state: 'checking' | 'ok' | 'degraded' | 'unreachable' = ready.isPending
    ? 'checking'
    : ready.isError
      ? 'unreachable'
      : ready.data?.ok
        ? 'ok'
        : 'degraded';

  const tone = {
    checking: 'border-border bg-muted/40',
    ok: 'border-success/40 bg-success/10',
    degraded: 'border-warning/50 bg-warning/10',
    unreachable: 'border-destructive/50 bg-destructive/10',
  }[state];

  const dot = {
    checking: 'bg-muted-foreground',
    ok: 'bg-success',
    degraded: 'bg-warning',
    unreachable: 'bg-destructive',
  }[state];

  const serverVersion = version.data?.version ?? version.data?.commit;

  return (
    <section
      aria-label={t('health.title')}
      className={cn('rounded-lg border p-4', tone)}
      data-testid="health-banner"
      data-state={state}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className={cn('size-2.5 shrink-0 rounded-full', dot)} aria-hidden="true" />
        <p className="text-sm font-medium">
          {state === 'checking' ? t('health.checking') : t(`health.${state}`)}
        </p>
        {ready.isFetching ? <Spinner className="size-3.5 text-muted-foreground" /> : null}

        <div className="ms-auto flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-[11px]" dir="ltr">
            {config.healthUrl}/ready
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void ready.refetch()}
            disabled={ready.isFetching}
          >
            {t('health.recheck')}
          </Button>
        </div>
      </div>

      <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
        <div className="flex gap-2">
          <dt className="font-medium">{t('health.apiBase')}</dt>
          <dd className="font-mono" dir="ltr">
            {config.apiUrl}
          </dd>
        </div>
        {serverVersion ? (
          <div className="flex gap-2">
            <dt className="font-medium">{t('health.version')}</dt>
            <dd className="font-mono" dir="ltr">
              {serverVersion}
            </dd>
          </div>
        ) : null}
      </dl>

      {/* Component detail is only returned to a privileged caller (07 §9.2); when
          the API is reachable from the docker network it may still arrive. */}
      {ready.data?.components ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {Object.entries(ready.data.components).map(([name, componentState]) => (
            <li key={name}>
              <Badge variant={componentState === 'up' ? 'success' : 'destructive'}>
                {name}: {componentState}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}

      {state === 'unreachable' || state === 'degraded' ? (
        <p className="mt-3 text-xs text-muted-foreground">{t('health.hint')}</p>
      ) : null}
    </section>
  );
}
